import { useState, useEffect } from "react";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { ptBR } from "@mui/x-data-grid/locales";
import { ICampaignBeneficiary, IMetric, IAPIBeneficiary } from "./Addressable.types";
import { cpfMask } from "utils/cpf";
import { Box, Button, CircularProgress, Modal } from "@mui/material";
import { Close } from "@mui/icons-material";
import { useAlert } from "contexts/alert";

type Props = {
  beneficiaries: IAPIBeneficiary[];
  onCampaignSent: (selectedBeneficiaries: IAPIBeneficiary[]) => void;
};

export function AddressableCampaignSend({ beneficiaries, onCampaignSent }: Props) {
  const { openAlert } = useAlert();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedWithoutEmail, setSelectedWithoutEmail] = useState<string[]>([]);
  const [isSending, setIsSending] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [metrics, setMetrics] = useState<IMetric[]>([
    { label: "Total", value: String(0) },
    { label: "Titulares", value: String(0) },
    { label: "Dependentes", value: String(0) },
    { label: "PJs", value: String(0) },
    { label: "CLTs", value: String(0) },
  ]);

  const formatPersonType = (type: string) => {
    if (!type) return "-";
    if (type === "PJ") return "Titular (PJ)";
    if (type === "CLT") return "Titular (CLT)";
    return type === "employee" ? "Titular" : "Dependente";
  };

  async function handleSendEmail() {
    try {
      setIsSending(true);
      setSelectedWithoutEmail([]);

      const selected = selectedIds.map(id => {
        const beneficiary = beneficiaries.find(person => person.EMPLOYEE_ID === id);
        if (beneficiary) return beneficiary;
        return null;
      }).filter(b => !!b) as IAPIBeneficiary[];

      // Validação se tem email
      const withoutEmail = selected.filter(b => !b?.EMPLOYEE_EMAIL).map(b => b.PERSON_TAX_PAYER_ID);

      if (!!withoutEmail.length) {
        setSelectedWithoutEmail(withoutEmail);
        setIsModalOpen(true);
        throw new Error("Os seguintes CPFs selecionados não têm email para envio de campanha.");
      }

      // Criar payload para envio (será usado quando houver API)
      const campaignPayload = {
        campaignType: "MISSING_DATA",
        beneficiaries: selected.map(b => ({
          email: b.EMPLOYEE_EMAIL,
          taxPayerId: b.PERSON_TAX_PAYER_ID,
        })),
      };

      console.log('Payload da campanha:', campaignPayload);

      // Chamar callback para criar campanha mockada
      onCampaignSent(selected);

      setSelectedIds([]);
      setSelectedWithoutEmail([]);

      openAlert("Campanha enviada com sucesso.", "success");
    } catch(err: any) {
      const message = err?.message || "Algo deu errado ao enviar a campanha."
      openAlert(message, "error");
    } finally {
      setIsSending(false);
    }
  }

  const columns: GridColDef<ICampaignBeneficiary>[] = [
    {
      field: "nomeCompleto",
      headerName: "Nome do beneficiário",
      width: 250,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "cpfBeneficiario",
      headerName: "CPF do beneficiário",
      width: 150,
      renderCell: (params) => params.value,
    },
    {
      field: "cpfTitular",
      headerName: "CPF do titular",
      width: 150,
      renderCell: (params) => params.value,
    },
    {
      field: "cnpj",
      headerName: "CNPJ",
      width: 150,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "tipoDependente",
      headerName: "Tipo do beneficiário",
      width: 150,
      renderCell: (params) => formatPersonType(params.value),
    },
    {
      field: "camposVazios",
      headerName: "Dados faltantes",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "email",
      headerName: "E-mail de envio",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
    {
      field: "telefone",
      headerName: "Telefone de envio",
      width: 200,
      renderCell: (params) => params.value || "-",
    },
  ];

  const rows: ICampaignBeneficiary[] = beneficiaries.map((beneficiary) => ({
    id: beneficiary.EMPLOYEE_ID,
    nomeCompleto: beneficiary.PERSON_NAME || 'Sem nome',
    cpfBeneficiario: beneficiary.PERSON_TAX_PAYER_ID ? cpfMask(beneficiary.PERSON_TAX_PAYER_ID) : '',
    cpfTitular: beneficiary.EMPLOYEE_TAX_PAYER_ID ? cpfMask(beneficiary.EMPLOYEE_TAX_PAYER_ID) : '',
    cnpj: beneficiary.ORGANIZATION_ID || '',
    tipoDependente: beneficiary.PERSON_TYPE || '',
    camposVazios: beneficiary.PERSON_TYPE || '',
    email: beneficiary.EMPLOYEE_EMAIL || '',
    telefone: beneficiary.EMPLOYEE_PHONE_NUMBER || '',
    selected: selectedIds.includes(beneficiary.EMPLOYEE_ID),
  }));

  useEffect(() => {
    const selectedBeneficiaries = selectedIds.map(id => {
      const person = beneficiaries.find(beneficiary => beneficiary.EMPLOYEE_ID === id);

      return {
        taxPayerId: person?.PERSON_TAX_PAYER_ID ?? null,
        type: person?.DEPENDENT_TYPE_ID ? "dependent" : "employee",
        isPj: person?.PERSON_TYPE === "PJ" ? true : false,
      }
    })
    .filter(beneficiary => !!beneficiary.taxPayerId)
    .flatMap(beneficiary => {
      const dependents = beneficiaries.filter(
        b => b.EMPLOYEE_TAX_PAYER_ID === beneficiary.taxPayerId && b.DEPENDENT_TYPE_ID !== null
      );

      return [
        beneficiary,
        ...dependents.map(d => ({
          taxPayerId: d.PERSON_TAX_PAYER_ID,
          type: "dependent",
          isPj: beneficiary.isPj ? true : false,
        })),
      ]
    });

    const total = selectedBeneficiaries.length;
    const titulares = selectedBeneficiaries.filter(b => b.type === "employee").length;
    const dependentes = selectedBeneficiaries.filter(b => b.type === "dependent").length;
    const pjs = selectedBeneficiaries.filter(b => b.isPj === true).length;
    const clts = selectedBeneficiaries.filter(b => b.isPj === false).length;

    setMetrics([
      { label: "Total", value: String(total) },
      { label: "Titulares", value: String(titulares) },
      { label: "Dependentes", value: String(dependentes) },
      { label: "PJs", value: String(pjs) },
      { label: "CLTs", value: String(clts) },
    ]);
  }, [selectedIds, beneficiaries]);

  return (
    <>
      <Modal open={isModalOpen}>
        <Box
          sx={{
            position: "absolute" as "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: "auto",
            width: "100%",
            maxWidth: 700,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: "1rem",
            border: "none",
            outline: "none",
            padding: "24px",
          }}
        >
          <div style={{"display":"flex","justifyContent":"space-between","alignItems":"center"}}>
            <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Erro ao enviar campanha</h6>
            <Close
              style={{"cursor":"pointer"}}
              onClick={() => setIsModalOpen(false)}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '24px' }}>
            <span style={{ fontSize: '16px', lineHeight: '150%', fontWeight: '400', color: '#191C1A' }}>
              Não foi possível enviar a campanha pois os seguintes CPFs não possuem e-mail para envio.
            </span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '24px' }}>
            {!!selectedWithoutEmail.length && (
              <span style={{ fontSize: '16px', lineHeight: '150%', fontWeight: '400', color: '#191C1A' }}>
                <strong>{selectedWithoutEmail.map(taxPayerId => cpfMask(taxPayerId)).join(", ")}</strong>
              </span>
            )}
          </div>

          <div style={{"display":"flex","paddingTop":"0.5rem","paddingBottom":"0.5rem","marginTop":"1rem","justifyContent":"flex-end","alignItems":"center","width":"100%"}}>
            <div style={{"display":"flex","gap":"0.25rem"}}>
              <Button
                variant='outlined'
                color='error'
                style={{"paddingTop":"0.25rem","paddingBottom":"0.25rem","paddingLeft":"1rem","paddingRight":"1rem","borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","lineHeight":"1.25rem","fontWeight":600}}
                onClick={() => setIsModalOpen(false)}
                disabled={isSending}
              >
                Verificar novamente
              </Button>
            </div>
          </div>
        </Box>
      </Modal>

      <div style={{ width: "100%", display: "flex", flexDirection: "row", gap: "16px", marginBottom: "30px" }}>
        {metrics.map((metric, index) => (
          <div key={index} style={{ width: '100%', border: 'solid 1px #E8E8E8', borderRadius: '16px', padding: '16px', display: 'flex', flexDirection: 'column' }}>
            <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: 0 }}>{metric.label}</p>
            <span style={{ fontSize: '20px', lineHeight: '32px', fontWeight: '600', color: '#6F6F78' }}>{metric.value}</span>
          </div>
        ))}
      </div>

      <DataGrid
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        isRowSelectable={(params) => {
          const beneficiary = beneficiaries.find(b => b.EMPLOYEE_ID === params.row.id);
          return !beneficiary?.DEPENDENT_TYPE_ID; // Só titulares podem ser selecionados
        }}
        onRowSelectionModelChange={(newSelection) => {
          const selectedIds = Array.isArray(newSelection)
            ? newSelection.map(id => String(id))
            : [];
          setSelectedIds(selectedIds);
        }}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 5,
            },
          },
        }}
        pageSizeOptions={[5, 10, 20, 50]}
        style={{ backgroundColor: "white", minHeight: 400 }}
        disableColumnFilter={false}
        disableColumnSelector={false}
        disableDensitySelector={false}
        scrollbarSize={10}
        sx={{
          "& .MuiDataGrid-row[data-rowselectable='false']": {
            "& .MuiCheckbox-root": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
            "& .MuiCheckbox-root.Mui-disabled": {
              opacity: 0.3,
              cursor: "not-allowed",
            },
          },
          "& .MuiDataGrid-checkboxInput.Mui-disabled": {
            opacity: 0.3,
            cursor: "not-allowed",
          },
        }}
      />

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px', gap: '16px', alignItems: 'center' }}>
        <span style={{ fontSize: '14px', marginRight: 'auto' }}>
          {selectedIds.length} beneficiário(s) selecionado(s)
        </span>
        <Button
          variant="contained"
          onClick={handleSendEmail}
          disabled={selectedIds.length === 0 || isSending}
        >
          {isSending ? <CircularProgress size={20} color="inherit" /> : 'Enviar campanha'}
        </Button>
      </div>
    </>
  );
}
